import axios from '@/lib/axios'
import { type CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'
import { startAuthentication, startRegistration } from '@simplewebauthn/browser'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

export interface PasskeyItem {
  id: number
  name: string
  created_at: string
  last_used_at: string | null
}

const csrfCookie = () => axios.get('/sanctum/csrf-cookie')

// ── login ──────────────────────────────────────────────────────────────────────

export const passkeyLogin = async (): Promise<void> => {
  // CSRF first — must be before login-options so the session that stores
  // the challenge is the same session that receives the POST
  await csrfCookie()

  const { data: opts } = await axios.get('/passkeys/login-options', {
    errorHandling: false,
  } as CustomAxiosRequestConfig)

  const assertion = await startAuthentication({ optionsJSON: opts })

  const assertionSlim = {
    id: assertion.id,
    rawId: assertion.rawId,
    type: assertion.type,
    response: {
      clientDataJSON: assertion.response.clientDataJSON,
      authenticatorData: assertion.response.authenticatorData,
      signature: assertion.response.signature,
      ...(assertion.response.userHandle ? { userHandle: assertion.response.userHandle } : {}),
    },
    clientExtensionResults: assertion.clientExtensionResults ?? {},
  }

  await axios.post(
    '/passkeys/login',
    { start_authentication_response: JSON.stringify(assertionSlim) },
    { errorHandling: false } as CustomAxiosRequestConfig,
  )
}

// ── API fns ────────────────────────────────────────────────────────────────────

const fetchPasskeys = (): Promise<PasskeyItem[]> =>
  axios.get<PasskeyItem[]>('/passkeys').then((r) => r.data)

const storePasskey = async (name: string): Promise<PasskeyItem> => {
  const { data: opts } = await axios.get('/passkeys/register-options', {
    errorHandling: false,
  } as CustomAxiosRequestConfig)

  const attResp = await startRegistration({ optionsJSON: opts })

  // Strip extra response fields added by @simplewebauthn/browser v9+
  // (publicKey, publicKeyAlgorithm, authenticatorData) — older laragear/webauthn rejects them
  const credential = {
    id: attResp.id,
    rawId: attResp.rawId,
    type: attResp.type,
    response: {
      clientDataJSON: attResp.response.clientDataJSON,
      attestationObject: attResp.response.attestationObject,
      ...(attResp.response.transports ? { transports: attResp.response.transports } : {}),
    },
    clientExtensionResults: attResp.clientExtensionResults ?? {},
  }

  await csrfCookie()
  const { data } = await axios.post<PasskeyItem>(
    '/passkeys/store-passkey',
    { passkey: JSON.stringify(credential), name },
    { errorHandling: false } as CustomAxiosRequestConfig,
  )
  return data
}

const removePasskey = (id: number): Promise<void> =>
  axios.delete(`/passkeys/${id}`).then(() => undefined)

// ── composable ─────────────────────────────────────────────────────────────────

export const usePasskeys = () => {
  const queryClient = useQueryClient()

  const { data: passkeys, isLoading, isError } = useQuery(
    ['passkeys'],
    fetchPasskeys,
    { refetchOnWindowFocus: false },
  )

  const registerMutation = useMutation((name: string) => storePasskey(name), {
    onSuccess: () => queryClient.invalidateQueries(['passkeys']),
  })

  const deleteMutation = useMutation((id: number) => removePasskey(id), {
    onSuccess: () => queryClient.invalidateQueries(['passkeys']),
  })

  return {
    passkeys,
    isLoading,
    isError,
    register: registerMutation.mutateAsync,
    registerLoading: registerMutation.isLoading,
    registerError: registerMutation.error,
    deletePasskey: deleteMutation.mutate,
    deletingId: deleteMutation.variables,
    deleteLoading: deleteMutation.isLoading,
  }
}
